import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  SUBURB_SERVICE_SEGMENTS,
  TargetCandidate,
  TargetCandidatesService,
} from './target-candidates.service';

/** A headed block in the dropdown ("Pages", "Services by region"). */
interface TargetGroup {
  label: string;
  items: TargetCandidate[];
}

/**
 * Link/button target field with an optional picker over the addresses the site
 * actually has (#199).
 *
 * A target is free text and stays free text — external URLs, anchors, `tel:`
 * and `mailto:` all have to keep working, and an author may legitimately link
 * to a page that does not exist yet. What this adds is a dropdown of known
 * addresses, so the common case (an internal page) need not be typed, plus a
 * warning when an internal-looking path matches nothing known.
 *
 * The warning does not block. `safeTarget` on the v3 side validates SHAPE, and
 * this validates EXISTENCE against a list that is knowingly incomplete (file
 * routes are invisible from Conductor — see TargetCandidatesService), so a
 * false "this page does not exist" is possible and must never stop a save.
 */
@Component({
  selector: 'app-target-input',
  templateUrl: './target-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TargetInputComponent),
      multi: true,
    },
  ],
})
export class TargetInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  /**
   * Callers place this in a flex row (`class="flex-1"`). Without an explicit
   * display the host is inline and the field collapses to its text width;
   * min-width:0 stops the long mono value from forcing the row wider than the
   * dialog.
   */
  @HostBinding('style.display') hostDisplay = 'block';
  @HostBinding('style.minWidth') hostMinWidth = '0';

  @Input() placeholder = '/contact or https://…';
  /**
   * Emits the picked page's title when the author selects from the dropdown,
   * so the caller can default an EMPTY label without overwriting a typed one.
   * The caller decides — this component never reaches across to the label.
   */
  @Output() pageSelected = new EventEmitter<TargetCandidate>();

  /** Structurally typed, not AutoComplete: see the #204 workaround below. */
  @ViewChild('ac') ac?: {
    overlayVisible?: boolean;
    hide?: () => void;
    inputEL?: { nativeElement: HTMLInputElement };
    value?: unknown;
  };

  value = '';
  /**
   * Grouped for the dropdown ([group]="true"): with 70-odd rows a flat list
   * buries the author's own pages under the generated region addresses.
   */
  suggestions: TargetGroup[] = [];

  private all: TargetCandidate[] = [];
  private knownPaths = new Set<string>();
  /** Suburb pages are recognised but not listed — see the service. */
  private suburbsByRegionPath = new Map<string, Set<string>>();
  /** No warning until the content records are actually in hand. */
  private loaded = false;
  private unsubscribe$ = new Subject<void>();

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private candidates: TargetCandidatesService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    document.addEventListener('mousedown', this.closeOnOutsideClick, true);
    this.candidates
      .knowledge$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (k) => {
          this.all = k.candidates;
          this.knownPaths = new Set(k.candidates.map((c) => c.path));
          this.suburbsByRegionPath = k.suburbsByRegionPath;
          this.loaded = k.loaded;
        },
        error: () => {},
      });
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.closeOnOutsideClick, true);
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * WORKAROUND (#204) — p-autoComplete already binds its own document click
   * listener on overlay show, but inside the content dialog it never fires
   * for us and the panel stayed open over the rest of the form. The cause was
   * not established, so this closes the panel instead of fixing it at the
   * layer where it breaks. Remove once #204 explains why the supported path
   * does not work; the structural @ViewChild type above goes with it.
   *
   * mousedown, not click, so it also fires when the press starts a text
   * SELECTION elsewhere in the dialog — the case in the bug report, where
   * text was selected with the panel still showing.
   *
   * Capture phase, because the dialog stops some events on the way up. The
   * panel is appended to <body>, so "inside" means either this host or the
   * overlay element.
   */
  private closeOnOutsideClick = (event: MouseEvent): void => {
    if (!this.ac?.overlayVisible) return;
    const target = event.target as Node | null;
    if (!target) return;
    if (this.host.nativeElement.contains(target)) return;
    if ((target as Element).closest?.('.p-autocomplete-panel')) return;
    this.ac.hide?.();
  };

  // ── ControlValueAccessor ───────────────────────────────────────────────

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // ── Picker ─────────────────────────────────────────────────────────────

  /**
   * p-autoComplete asks for suggestions on both typing and the dropdown
   * button; an empty query means "show me everything", which is what makes
   * this browsable rather than search-only.
   */
  search(event: { query?: string }): void {
    const q = (event?.query || '').trim().toLowerCase();
    // Match on address OR title: authors think "the FAQ page" as often as
    // they think "/faq". An empty query means "show me everything", which is
    // what makes the dropdown browsable rather than search-only.
    const matches = !q
      ? this.all
      : this.all.filter(
          (c) => c.path.toLowerCase().includes(q) || c.title.toLowerCase().includes(q)
        );
    this.suggestions = this.groupBySource(matches);
  }

  /**
   * Pages first: they are the author's own records, and the generated region
   * addresses would otherwise outnumber them better than two to one.
   */
  private groupBySource(list: TargetCandidate[]): TargetGroup[] {
    const order = ['Pages', 'Regions', 'Services by region'];
    const byGroup = new Map<string, TargetCandidate[]>();
    for (const c of list) {
      const rows = byGroup.get(c.group) ?? [];
      rows.push(c);
      byGroup.set(c.group, rows);
    }
    return order
      .filter((g) => byGroup.get(g)?.length)
      .map((label) => ({ label, items: byGroup.get(label)! }));
  }

  /**
   * In free-text mode p-autoComplete hands back either a typed string or the
   * selected object, so the object case is narrowed to its path here — letting
   * an object reach the model would write "[object Object]" into the target.
   */
  onModelChange(next: string | TargetCandidate): void {
    if (next && typeof next === 'object') {
      // Grouping means a group header can reach here; it has no path, and
      // writing `undefined` would silently blank the author's target.
      const path = (next as TargetCandidate).path;
      if (typeof path !== 'string') return;
      this.value = path;
      this.onChange(path);
      return;
    }
    // A string by here (an object returned above); the cast is for the scripts/ harnesses, which compile with strict off.
    this.value = (next as string) ?? '';
    this.onChange(this.value);
  }

  onSelect(candidate: TargetCandidate): void {
    if (!candidate || typeof candidate.path !== 'string') return;
    this.value = candidate.path;
    this.onChange(candidate.path);

    // p-autoComplete's selectItem() has just put the SUGGESTION OBJECT into
    // its own value and written it straight to the input element, which the
    // DOM renders as "[object Object]". Its `field` input would format that,
    // but `field` is also used for a plain string value — where it resolves
    // to nothing and blanks an existing target on load. Neither setting is
    // right for both, and `field` only accepts a string, so the object is
    // reduced to its path here instead, right after the write.
    if (this.ac) {
      this.ac.value = candidate.path;
      if (this.ac.inputEL) this.ac.inputEL.nativeElement.value = candidate.path;
    }

    this.pageSelected.emit(candidate);
  }

  onBlur(): void {
    this.onTouched();
  }

  // ── Warning ────────────────────────────────────────────────────────────

  /**
   * True when the target looks like an internal page address and matches no
   * address we know of. Anchors, external URLs, tel: and mailto: are none of
   * this component's business, and an empty field is not yet wrong.
   */
  get unknownPath(): boolean {
    const v = (this.value || '').trim();
    if (!v || !v.startsWith('/')) return false;
    // A path with an anchor still names a page: /faq#costs → /faq.
    const path = v.split('#')[0].split('?')[0].replace(/\/+$/, '') || '/';
    // Until the content records are loaded every address looks unknown, and
    // warning then flags the whole form. Gate on the load having HAPPENED,
    // not on the list being non-empty: a half-loaded list is still wrong.
    if (!this.loaded) return false;
    if (this.knownPaths.has(path)) return false;
    return !this.isSuburbPage(path);
  }

  /**
   * /{state}/{region}/{service}/{suburb} — real pages, deliberately absent
   * from the dropdown because there are several hundred. Recognised here so
   * an author who types one is not told their correct address is wrong.
   */
  private isSuburbPage(path: string): boolean {
    const parts = path.split('/').filter(Boolean);
    if (parts.length !== 4) return false;
    const [state, region, service, suburb] = parts;
    if (!SUBURB_SERVICE_SEGMENTS.includes(service)) return false;
    const subs = this.suburbsByRegionPath.get(`/${state}/${region}`);
    return !!subs?.has(suburb);
  }
}
